/**
 * HLOS-13 — Data flows que cruzam a fronteira TS ↔ Py (via Pyodide).
 *
 * Inspirado em `DATA_FLOWS` da skill `pyodide-bridge`: este módulo é o
 * ponto único onde vivem os tipos serializáveis que entram/saem do
 * kernel Python. Tudo o que cruza Pyodide DEVE ser definido aqui.
 *
 * Princípios:
 *  - **Type safety sem `any`** (sem @ts-ignore): todos os retornos
 *    do kernel são validados por type guards (`isKernelResponse`, etc).
 *  - **Discriminated unions**: erros constitucionais são first-class,
 *    não exceptions genéricos.
 *  - **JSON-only**: nada de PyProxy, callbacks ou funções. O boundary
 *    é puramente dados.
 *  - **CL-07/CL-08/Princípio XI**: zero θ_lat, zero PII, zero nome de
 *    provedor cruzam esta fronteira.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Erro constitucional (TS-side, espelha o que o Python lança via JSON)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Exceção lançada quando o kernel detecta violação constitucional
 * (CC-01..08, Princípio X, etc). Mapeada de `KernelResponse.kind ===
 * "constitutional_violation"` para um erro TS tipado.
 *
 * Caller trata via `catch (e) instanceof ConstitutionalViolationError`
 * (tipicamente, traduz para R3 silent + reply genérico — zero penalidade).
 */
export class ConstitutionalViolationError extends Error {
  readonly violations: readonly string[];
  constructor(violations: readonly string[]) {
    super(`ConstitutionalViolation: ${violations.join(", ")}`);
    this.name = "ConstitutionalViolationError";
    this.violations = violations;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Kernel response (formato canônico retornado pelo Python)
// ─────────────────────────────────────────────────────────────────────────────

/** Discriminated union: o que o kernel Python pode retornar. */
export type KernelResponse =
  | { readonly kind: "ok"; readonly result: ProcessTurnResult }
  | {
      readonly kind: "constitutional_violation";
      readonly violations: readonly string[];
      readonly cl_refs?: readonly string[]; // ex: ["CL-07", "CC-05"]
    };

/** Type guard: valida que `raw` é um `KernelResponse` válido. */
export function isKernelResponse(raw: unknown): raw is KernelResponse {
  if (typeof raw !== "object" || raw === null) return false;
  const obj = raw as { kind?: unknown };
  if (obj.kind === "ok") {
    return isProcessTurnResult((raw as { result?: unknown }).result);
  }
  if (obj.kind === "constitutional_violation") {
    const v = (raw as { violations?: unknown }).violations;
    return Array.isArray(v) && v.every((x) => typeof x === "string");
  }
  return false;
}

/** Type guard: valida `ProcessTurnResult` cru. */
export function isProcessTurnResult(raw: unknown): raw is ProcessTurnResult {
  if (typeof raw !== "object" || raw === null) return false;
  const r = raw as Partial<ProcessTurnResult>;
  if (typeof r.reply !== "string") return false;
  if (typeof r.rLevel !== "string") return false;
  if (!isRLevel(r.rLevel)) return false;
  if (r.initiativeText !== null && typeof r.initiativeText !== "string") return false;
  if (!Array.isArray(r.curiosityGaps)) return false;
  if (r.curiosityGaps.length > 3) return false; // CL-12 cap
  return true;
}

/** Type guard: valida `RLevel`. */
export function isRLevel(v: unknown): v is RLevel {
  return v === "R1" || v === "R2" || v === "R3" || v === "R4" || v === "R5" || v === "R6";
}

// ─────────────────────────────────────────────────────────────────────────────
// Tipos de input/output (re-exportados via flows; antes ficavam em types.ts)
// ─────────────────────────────────────────────────────────────────────────────

/** Níveis de iniciativa do Companion (CL-06 + Princípio V). */
export type RLevel = "R1" | "R2" | "R3" | "R4" | "R5" | "R6";

/**
 * Turno do aprendiz — entrada para o kernel.
 * Mesma estrutura que o Learner envia ao kernel CPython nativo.
 */
export interface UserTurnAttachment {
  readonly kind: "image" | "sketch";
  readonly dataUrl: string;
  readonly filename?: string;
}

export interface UserTurn {
  /** Texto do turno (PII removida antes de chegar aqui — ver CL-08). */
  readonly text: string;
  /** Contexto opcional: tópico, profundidade percebida. Sem θ_lat. */
  readonly context?: UserTurnContext;
  /** HLOS-323: anexos inline (imagem/sketch). Default: nenhum. */
  readonly attachments?: readonly UserTurnAttachment[];
}

/** Contexto opcional do turno (dados públicos ao kernel). */
export interface UserTurnContext {
  /** Tópico do atom de conhecimento (não-sensível). */
  readonly topic?: string;
  /** Profundidade percebida (0.0 a 1.0). Sem θ_lat. */
  readonly ka_depth?: number;
}

/**
 * Resultado do processamento do turno pelo kernel Python.
 * Mesmo formato retornado por `hlos.kernel.engine.process_turn` no CPython nativo.
 */
export interface ProcessTurnResult {
  /** Resposta gerada pelo Companion. */
  readonly reply: string;
  /** Nível R (R1..R6) ativo nesta resposta — gate R1-R6 (CL-06). */
  readonly rLevel: RLevel;
  /** Texto da iniciativa se rLevel !== 'silent' (CL-06, R5: convite, não instrução). */
  readonly initiativeText: string | null;
  /** Atualização do working memory (3 curiosity gaps cap — CL-12). */
  readonly curiosityGaps: readonly string[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Result<T, E> — discriminated union para processTurnStrict
// ─────────────────────────────────────────────────────────────────────────────

/** Resultado discriminado sem exceptions (para quem prefere evitar try/catch). */
export type Result<T, E> =
  | { readonly ok: true; readonly value: T }
  | { readonly ok: false; readonly error: E };

/** Erro de fronteira TS↔Py. */
export type BridgeError =
  | { readonly kind: "constitutional"; readonly violations: readonly string[]; readonly cl_refs?: readonly string[] }
  | { readonly kind: "technical"; readonly message: string };

/**
 * Versão strict de `processTurn` que retorna `Result<T, BridgeError>` em vez
 * de lançar. Útil para callers que preferem evitar try/catch (e.g., quando
 * o caller é uma função pura, ou para fazer batch processing de turnos).
 *
 * Equivalente semântico a `processTurn`, exceto pela forma de erro.
 */
export type ProcessTurnStrictResult = Result<ProcessTurnResult, BridgeError>;

// ─────────────────────────────────────────────────────────────────────────────
// Privacy boundary — validador (CL-07, CL-08)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Strings NUNCA permitidas em dados que cruzam a fronteira TS↔Py.
 * Aplicado em runtime pelo bridge antes de serializar para JSON.
 *
 * Inspirado em `hlos.privacy.boundary.ALLOWED_FOR_LLM` (Python side).
 */
export const FORBIDDEN_BOUNDARY_STRINGS: readonly string[] = Object.freeze([
  "theta_lat",
  "θ_lat",
  "memory_store",
  "goal_store",
  "learner_id",
  "learner_name",
]);

/** Lança `ConstitutionalViolationError` se `payload` contém boundary strings. */
export function assertNoForbiddenBoundary(
  payload: unknown,
  path: string = "payload",
): void {
  const seen = scanForForbidden(payload);
  if (seen.length > 0) {
    throw new ConstitutionalViolationError(
      seen.map((s) => `${path} contains forbidden boundary string: "${s}"`),
    );
  }
}

function scanForForbidden(value: unknown, out: string[] = []): string[] {
  if (typeof value === "string") {
    for (const forbidden of FORBIDDEN_BOUNDARY_STRINGS) {
      if (value.toLowerCase().includes(forbidden.toLowerCase())) {
        out.push(forbidden);
      }
    }
    return out;
  }
  if (Array.isArray(value)) {
    for (const item of value) scanForForbidden(item, out);
    return out;
  }
  if (typeof value === "object" && value !== null) {
    // Per CL-07/CL-08: tanto a chave quanto o valor podem conter boundary strings.
    // Ex: `{ theta_lat: [...] }` tem "theta_lat" como key.
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      for (const forbidden of FORBIDDEN_BOUNDARY_STRINGS) {
        if (k.toLowerCase().includes(forbidden.toLowerCase())) {
          out.push(forbidden);
        }
      }
      scanForForbidden(v, out);
    }
  }
  return out;
}
